import { DOCUMENT } from '@angular/common';
import { EnvironmentProviders, inject, Injectable, provideAppInitializer } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AdminAnnouncerService } from '../a11y/admin-announcer.service';
import { focusPageHeading, pagePath } from './page-focus';

/** Where page headings live: the admin layout's content area. */
const HEADING = '.admin-container h1';

/**
 * Opening a different admin page moves focus to its heading and announces it
 * ("Opened Reservations"). A list's filters change only the query string, so
 * typing in its search box keeps focus there. One app-wide service rather than
 * part of the layout: the layout is rebuilt on every top-level page, and the
 * outgoing and incoming layouts both saw the same navigation. Started by
 * `provideAdminPageFocus()`.
 */
@Injectable({ providedIn: 'root' })
export class AdminPageFocusService {
  private readonly document = inject(DOCUMENT);
  private lastPath?: string;
  private navigation = 0;
  /** The old page's headings, which can still be on screen when the navigation ends. */
  private leaving: ReadonlySet<Element> = new Set();
  private readonly announcer = inject(AdminAnnouncerService);

  constructor() {
    inject(Router).events.subscribe((e) => {
      if (e instanceof NavigationStart) this.leaving = new Set(Array.from(this.document.querySelectorAll(HEADING)));
      if (!(e instanceof NavigationEnd)) return;
      const path = pagePath(e.urlAfterRedirects);
      const opened = e.id > 1 && path !== this.lastPath;
      this.lastPath = path;
      if (opened) void this.focusPage(++this.navigation);
    });
  }

  private async focusPage(navigation: number): Promise<void> {
    const doc = this.document;
    const before = doc.activeElement;
    const stale = () => navigation !== this.navigation;
    // Give up if the admin has already clicked or tabbed into the new page while its heading loaded.
    const cancelled = () =>
      stale() ||
      (doc.activeElement !== before &&
        !!doc.activeElement?.closest('.admin-container') &&
        !this.leaving.has(doc.activeElement));
    const title = await focusPageHeading(doc, cancelled, undefined, this.leaving, HEADING);
    if (title && !stale()) this.announce(`Opened ${title}`);
  }

  private announce(text: string): void {
    this.announcer.announce(text);
  }
}

/** Starts heading focus on navigation; add to the admin app's providers. */
export function provideAdminPageFocus(): EnvironmentProviders {
  return provideAppInitializer(() => {
    inject(AdminPageFocusService);
  });
}
